import React from 'react'

export interface ButtonProps {
  children: React.ReactNode
  variant?: 'primary' | 'ghost' | 'secondary' | 'nav' | 'card-primary' | 'card-outline'
  size?: 'sm' | 'default' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  href?: string
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  className?: string
  'aria-label'?: string
  title?: string
}

/**
 * Button -- Editorial Grid (Concept 7)
 *
 * Variants:
 * - primary: burnt orange fill, white text
 * - ghost: transparent with sage border
 * - nav: deep forest fill, white text, compact
 * - card-primary: deep forest fill, full width
 * - card-outline: transparent with border, full width
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'default',
  disabled = false,
  loading = false,
  fullWidth = false,
  href,
  onClick,
  type = 'button',
  className = '',
  'aria-label': ariaLabel,
  title,
}) => {
  const base = [
    'inline-flex items-center justify-center gap-2',
    'font-body font-semibold rounded-eg-md',
    'transition-all duration-200 ease-out',
    'cursor-pointer select-none',
    'no-underline',
  ]

  const sizeClasses =
    size === 'lg'
      ? 'text-eg-md px-8 py-3.5'
      : size === 'sm'
        ? 'text-eg-xs px-3 py-1.5'
        : 'text-eg-sm px-5 py-2'

  const variantMap: Record<string, string[]> = {
    primary: [
      'bg-eg-accent border border-transparent text-white',
      'hover:bg-eg-accent-hover hover:-translate-y-px',
    ],
    ghost: [
      'bg-transparent border-[1.5px] border-eg-border text-eg-text-secondary font-medium px-7 py-[13px]',
      'hover:border-eg-text-secondary hover:text-eg-text',
    ],
    secondary: [
      'bg-transparent border-[1.5px] border-eg-border text-eg-text-secondary font-medium px-7 py-[13px]',
      'hover:border-eg-text-secondary hover:text-eg-text',
    ],
    nav: [
      'bg-eg-primary border border-transparent text-white',
      'text-eg-sm px-5 py-2',
      'hover:bg-eg-primary-light',
    ],
    'card-primary': [
      'bg-eg-primary border border-transparent text-white w-full',
      'hover:bg-eg-primary-light',
    ],
    'card-outline': [
      'bg-transparent border-[1.5px] border-eg-border text-eg-text w-full',
      'hover:border-eg-text-secondary',
    ],
  }

  const variantClasses = variantMap[variant] || variantMap.primary

  const disabledClasses = disabled || loading
    ? 'opacity-50 pointer-events-none'
    : ''

  const widthClass = fullWidth ? 'w-full' : ''

  const cls = [
    ...base,
    sizeClasses,
    ...variantClasses,
    disabledClasses,
    widthClass,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  if (href && !disabled) {
    return (
      <a href={href} className={cls}>
        {loading && <Spinner />}
        {children}
      </a>
    )
  }

  return (
    <button
      type={type}
      className={cls}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
    >
      {loading && <Spinner />}
      {children}
    </button>
  )
}

function Spinner() {
  return (
    <svg
      className="animate-spin -ml-1 mr-1 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  )
}
